import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import PlayCircleOutlineIcon from '@material-ui/icons/PlayCircleOutline';
import { useDispatch } from 'react-redux';
import allActions from '../../../actions';
import { OnboardingVideosAPI } from '../../../API/onboarding_videos';
import { tabs, unoTheme } from '../../../assets/styles/unoTheme';
import { MyTab, MyTabs } from '../../utils/MyTabs';

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
    },
    tabs: tabs,
    tabsWithRight: {
        display: 'flex',
        flexDirection: 'row',
        paddingRight: '16px',
        alignItems: 'center',
    },
    content: {
        width: '100%',
        padding: '16px',
        flex: 1,
        overflowY: 'auto',
    },
    sectionTitle: {
        color: unoTheme.mainColor,
        fontWeight: 600,
        marginBottom: '16px',
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 260px))',
        justifyContent: 'flex-start',
        gap: '12px',
    },
    card: {
        borderRadius: '12px',
        border: `1px solid ${unoTheme.borderInputColor}`,
        boxShadow: 'none',
        cursor: 'pointer',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
            boxShadow: '0 10px 24px rgba(0, 73, 198, 0.12)',
            transform: 'translateY(-2px)',
        },
    },
    thumbnailWrapper: {
        position: 'relative',
        width: '100%',
        paddingTop: '56.25%',
        background: unoTheme.menuContent,
        borderRadius: '12px 12px 0 0',
        overflow: 'hidden',
    },
    thumbnail: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },
    playButton: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        background: unoTheme.mainColor,
        color: 'white',
        '&:hover': {
            background: unoTheme.hoverMainColor,
        },
    },
    playIcon: {
        fontSize: 36,
    },
    cardContent: {
        padding: '10px 12px 12px',
        '&:last-child': {
            paddingBottom: '12px',
        },
    },
    cardTitle: {
        color: unoTheme.mainColor,
        fontWeight: 600,
        fontSize: '0.95rem',
    },
    cardSubtitle: {
        color: unoTheme.defaultTextColor,
        fontSize: '0.78rem',
        marginTop: '2px',
        textAlign: 'left',
        display: '-webkit-box',
        WebkitLineClamp: 5,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    dialogTitle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingRight: '8px',
    },
    dialogTitleText: {
        color: unoTheme.mainColor,
        fontWeight: 600,
    },
    dialogPaper: {
        borderRadius: '16px',
    },
    dialogContent: {
        paddingTop: '8px',
    },
    videoWrapper: {
        position: 'relative',
        width: '100%',
        paddingTop: '56.25%',
        background: '#000',
        borderRadius: '12px',
        overflow: 'hidden',
    },
    iframe: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        border: 0,
    },
    videoSubtitle: {
        marginTop: '12px',
        padding: '0 8px 8px',
        color: unoTheme.defaultTextColor,
    },
}));

const DEFAULT_CATEGORIES = [
    {
        id: 'boas_vindas',
        label: 'Boas-vindas',
        videos: [],
    },
    {
        id: 'gestao_invest_rpps',
        label: 'Gestão de investimentos Conheça os serviços LEMA',
        videos: [],
    },
    {
        id: 'educacao_governanca',
        label: 'Governança',
        videos: [],
    },
    {
        id: 'capacitation',
        label: 'Capacitação: Conte com a LEMA Edu',
        videos: [],
    },
];

const normalizeCategories = (apiCategories) => {
    const baseMap = new Map(
        DEFAULT_CATEGORIES.map((category) => [category.id, { ...category, videos: [] }])
    );
    const extraCategories = [];

    apiCategories.forEach((category) => {
        const key = category.key || category.category_key;
        const label = category.label || category.category_label;
        const videos = (category.videos || [])
            .map((video) => ({
                id: video.id,
                title: video.title,
                subtitle: video.subtitle || '',
                embedUrl: video.embed_url || video.embedUrl,
                thumbnail: video.thumbnail_url || video.thumbnail,
                displayOrder: video.display_order ?? 0,
            }))
            .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));

        if (baseMap.has(key)) {
            const current = baseMap.get(key);
            baseMap.set(key, {
                ...current,
                label: label || current.label,
                videos,
            });
        } else if (key) {
            extraCategories.push({
                id: key,
                label: label || key,
                videos,
            });
        }
    });

    return [...DEFAULT_CATEGORIES.map((category) => baseMap.get(category.id)), ...extraCategories];
};

export default function VideosScreen() {
    const classes = useStyles();
    const dispatch = useDispatch();
    const [tabValue, setTabValue] = useState(0);
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    const currentCategory = useMemo(() => categories[tabValue], [categories, tabValue]);

    const handleChangeTabValue = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleOpenVideo = (video) => {
        setSelectedVideo(video);
    };

    const handleCloseVideo = () => {
        setSelectedVideo(null);
    };

    useEffect(() => {
        let isMounted = true;

        const loadVideos = async () => {
            setIsLoading(true);
            setHasError(false);
            const response = await OnboardingVideosAPI.listActiveOnboardingVideos();

            if (!isMounted) return;

            if (response.success) {
                const apiCategories = response.body?.categories ?? [];
                setCategories(normalizeCategories(apiCategories));
            } else {
                setHasError(true);
                dispatch(allActions.mainAlertActions.setMainAlert({
                    show: true,
                    type: 'error',
                    message: 'Erro ao carregar vídeos de onboarding',
                }));
            }

            setIsLoading(false);
        };

        loadVideos();

        return () => {
            isMounted = false;
        };
    }, [dispatch]);

    useEffect(() => {
        if (tabValue >= categories.length) {
            setTabValue(0);
        }
    }, [categories, tabValue]);

    return (
        <div className={classes.root}>
            <div className={classes.tabsWithRight}>
                <div className={classes.tabs}>
                    <MyTabs
                        value={tabValue}
                        indicatorColor="primary"
                        textColor="primary"
                        onChange={handleChangeTabValue}
                        aria-label="Categorias de vídeos">
                        {categories.map((category) => (
                            <MyTab key={category.id} label={category.label} />
                        ))}
                    </MyTabs>
                </div>
            </div>

            <div className={'defaultScrollWithHeaderContent'}>
                <div className={classes.content}>
                    {isLoading ? (
                        <Typography variant="body2" className={classes.cardSubtitle}>
                            Carregando vídeos...
                        </Typography>
                    ) : hasError ? (
                        <Typography variant="body2" className={classes.cardSubtitle}>
                            Não foi possível carregar os vídeos.
                        </Typography>
                    ) : currentCategory?.videos?.length ? (
                        <div className={classes.grid}>
                            {currentCategory?.videos?.map((video) => (
                                <Card
                                    key={video.id}
                                    className={classes.card}
                                    onClick={() => handleOpenVideo(video)}>
                                    <div className={classes.thumbnailWrapper}>
                                        <img className={classes.thumbnail} src={video.thumbnail} alt={video.title} />
                                        <IconButton
                                            className={classes.playButton}
                                            aria-label={`Reproduzir ${video.title}`}
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                handleOpenVideo(video);
                                            }}>
                                            <PlayCircleOutlineIcon className={classes.playIcon} />
                                        </IconButton>
                                    </div>
                                    <CardContent className={classes.cardContent}>
                                        <Typography variant="subtitle1" className={classes.cardTitle}>
                                            {video.title}
                                        </Typography>
                                        <Typography variant="body2" className={classes.cardSubtitle}>
                                            {video.subtitle}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    ) : (
                        <Typography variant="body2" className={classes.cardSubtitle}>
                            Nenhum vídeo disponível nesta categoria.
                        </Typography>
                    )}
                </div>
            </div>

            <Dialog
                open={Boolean(selectedVideo)}
                onClose={handleCloseVideo}
                maxWidth="md"
                fullWidth
                classes={{ paper: classes.dialogPaper }}>
                <DialogTitle disableTypography className={classes.dialogTitle}>
                    <Typography variant="h6" className={classes.dialogTitleText}>
                        {selectedVideo?.title}
                    </Typography>
                    <IconButton aria-label="Fechar" onClick={handleCloseVideo}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent className={classes.dialogContent}>
                    <div className={classes.videoWrapper}>
                        {selectedVideo && (
                            <iframe
                                title={selectedVideo.title}
                                src={selectedVideo.embedUrl}
                                className={classes.iframe}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            />
                        )}
                    </div>
                    <Typography variant="body2" className={classes.videoSubtitle}>
                        {selectedVideo?.subtitle}
                    </Typography>
                </DialogContent>
            </Dialog>
        </div>
    );
}
